import { useEffect, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner, SearchableSelect, UniversalPageLayout } from "../../components/BaseComponents";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import routes from "../../components/Router/routes";
import useApi from "../../hooks/useApi";
import useCurrencyFormatter from "../../hooks/useCurrencyFormatter";
import useDataSort from "../../hooks/useDataSort";
import usePagination from "../../hooks/usePagination";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { centsToCurrency } from "../../lib/utils";
import { useProjectDetailZones } from "./useProjectDetailZones";
import CompanyProjectEditModal from "./CompanyProjectEditModal";

function CompanyProject() {
    const { projectId } = useParams();

	const formatCurrency = useCurrencyFormatter();
    const [listData, setListData ] = useState<any[]>([])
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);

    const [summary, setSummary] = useState<any>({});

    const [grouping, setGrouping] = useState<'purchases'|'suppliers'>('suppliers');

    const [scope, setScope] = useState('');

	const [editCandidate, setEditCandidate] = useState<any>(null);
    const dataSort = useDataSort('supplierName', 'desc');
    const pagination = usePagination(1, 20);

    // Debounce searchText so typing doesn't fire an API request on every keystroke
    const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchText, () => setDebouncedSearchText(searchText))

    useEffect(() => {
        const loadListdata = async () => {
            const result = await api.get('/projects?sortKey=name&sortDirection=asc');
            setListData(result.data.rows);
        }
        loadListdata();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLoadingSummary(true);
        const loadSummary = async () => {
            const result = await api.get(`/projects/${projectId}`);
            setSummary(result.projectSummary)
            setLoadingSummary(false);
        }

        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    const columns: TableColumn[] = grouping === 'purchases'
        ? [
            {
                id:'itemName',
                label: 'Item Name',
                sortable: true,
            },
            {
                id:'level1',
                label: 'Emission Type',
                sortable: true,
            },
            {
                id:'totalSpend',
                label: 'Total Spend',
                sortable: true,
                renderer: (key, data) => <span>{ formatCurrency(Number(centsToCurrency(data[key]))) }</span>,
                textAlign: 'right',
            },
            {
                id:'totalEmissions',
                label: 'CO₂e Emissions',
                sortable: true,
                renderer: (key, data) => <span>{ (data[key]) } kgCO₂e</span>,
                textAlign: 'right',
            }
        ]
        : [
            {
                id:'supplierName',
                label: 'Supplier Name',
                sortable: true,
            },
            {
                id:'totalSpend',
                label: 'Total Spend',
                sortable: true,
                renderer: (key, data) => <span>{ formatCurrency(Number(centsToCurrency(data[key]))) }</span>,
                textAlign: 'right',
            },
            {
                id:'totalEmissions',
                label: 'CO₂e Emissions',
                sortable: true,
                renderer: (key, data) => <span>{ (data[key]) } kgCO₂e</span>,
                textAlign: 'right',
            },
            {
                id:'invite',
                label: '',
            }
        ]

    const api = useApi();

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const run = async () => {
            const qs = new URLSearchParams({
                grouping,
                searchText: debouncedSearchText,
                ...dataSort.params,
                ...pagination.params,
                scope,
            })
            const results = await api.get(`/projects/${projectId}/entries?` + qs)
            if(cancelled) {
                return;
            }
            setLoading(false);
            setData(results.data.rows);
            pagination.readMeta(results.data.meta);
        }
        run();
        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params, grouping, projectId, dataSort.params, scope, debouncedSearchText])

    const navigate = useNavigate();

    const projectSelector = (
        <SearchableSelect
            options={listData.map((project) => ({ value: String(project.id), label: project.name }))}
            isSearchable={false}
            isClearable={false}
            onChange={(option) => {
                if (option) {
                    navigate(generatePath(routes.companyProjects.path, { projectId: option.value }))
                }
            }}
            value={listData
                .map((project) => ({ value: String(project.id), label: project.name }))
                .filter((option) => option.value === String(projectId ?? ''))}
        />
    )

    const tableWithPagination = (
        <>
            <SimpleSortTable
                columns={columns}
                data={data}
                isLoading={loading}
                defaultSorting={dataSort.defaultSorting}
                onSortPress={dataSort.onChange}
            />
            <Pagination
                pagination={pagination.pagination}
                onPageChange={pagination.onPageChange}
                isLoading={loading}
            />
        </>
    )

    // Find current project from listData (has name, code, totalTurnoverAmount) for edit modal
    const currentProject = listData.find(p => p.id === projectId)

    const zones = useProjectDetailZones({
        projectSelector,
        summary,
        scope,
        onScopeChange: (id) => setScope(id),
        grouping,
        onGroupingChange: (newGrouping) => {
            setGrouping(newGrouping)
            dataSort.onChange(newGrouping === 'suppliers' ? 'supplierName' : 'itemName', 'ascending')
        },
        searchText,
        onSearchChange: (event) => setSearchText(event.target.value),
        onSearchKeyDown: (event) => {
            if (event.key === 'Enter') {
                flushSearch()
            }
        },
        onSearchClear: () => {
            setSearchText('')
            flushSearch()
        },
        searchInProgress,
        tableComponent: tableWithPagination,
        isLoading: loading,
        onEditClick: () => setEditCandidate(currentProject),
    })

    if(loadingSummary) {
        return <LoadingSpinner />
    }

    return (
        <>
            <UniversalPageLayout
                documentTitle={routes.companyProjects.name}
                heading={{
                    crumbs: [
                        { label: 'Purchases', route: routes.companyPurchases.path },
                        { label: routes.companyProjects.menuName as string, route: generatePath(routes.companyProjects.path, { projectId: undefined }) },
                        { label: 'Views' },
                    ],
                }}
                primarySelector={zones.primarySelector}
                viewSelector={zones.viewSelector}
                summary={zones.summary}
                tabs={zones.tabs}
                filtersSimple={zones.filtersSimple}
                actions={zones.actions}
            >
                {zones.table}
            </UniversalPageLayout>
            {editCandidate && (
                <CompanyProjectEditModal
                    open={!!editCandidate}
                    onClose={() => setEditCandidate(null)}
                    projectId={editCandidate.id}
                    projectName={editCandidate.name}
                    projectCode={editCandidate.code}
                    projectValue={editCandidate.totalTurnoverAmount}
                    onEdited={() => {
                        setEditCandidate(null)                        // Reload project list to get updated data
                        const loadListdata = async () => {
                            const result = await api.get('/projects?sortKey=name&sortDirection=asc');
                            setListData(result.data.rows);
                        }
                        loadListdata();
                        // Reload project summary                        setLoadingSummary(true)
                        const loadSummary = async () => {
                            const result = await api.get(`/projects/${projectId}`);
                            setSummary(result.projectSummary)
                            setLoadingSummary(false);
                        }
                        loadSummary();
                    }}
                />
            )}
        </>
    )
}

export default CompanyProject;
